import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { BASE_API, USER_TOKEN } from 'config/consts';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import JsPDF from 'jspdf';
import VoucherModal from 'common/components/navigation/actionsTab/feedback/presentialCourseFeedback';
import { buildExcelFileName, buildPdfFileName } from './reportFileName';

// Função auxiliar para formatar a data
const formatDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}-${month}-${year}`;
};

const ReportList = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros de backend
  const [filterEntidade, setFilterEntidade] = useState('Todas');
  const [filterFormato, setFilterFormato] = useState('Entidades');
  const [filterForma, setfFilterForma] = useState('Vouchers');
  const [filterAtividade, setFilterAtividade] = useState('Todas');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterStartDateReport, setFilterStartDateReport] = useState('');
  const [filterEndDateReport, setFilterEndDateReport] = useState('');
  const [filterPosto, setFilterPosto] = useState('Todas');

  // Filtros de frontend
  const [filterText, setFilterText] = useState(''); // Este será o filtro local de nome
  const [filterSelected, setFilterSelected] = useState('Todos');

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Download
  const [downloadFormat, setDownloadFormat] = useState('PDF');

  // Função para lidar com a seleção individual do checkbox
  const handleCheckboxChange = useCallback((id) => {
    setReports((prevReports) =>
      prevReports.map((report) =>
        report.id === id ? { ...report, selected: !report.selected } : report
      )
    );
  }, []);

  // Funções de navegação da paginação
  const paginate = useCallback((pageNumber) => setCurrentPage(pageNumber), []);
  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, totalPages]);
  const handlePrevPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  // Função para obter o token de autorização
  const getAuthHeader = useCallback(() => {
    try {
      const token = localStorage.getItem(USER_TOKEN);
      return token ? `${JSON.parse(token)}` : '';
    } catch (e) {
      console.error('Erro ao parsear o token do localStorage:', e);
      return '';
    }
  }, []);

  const gerarExcel = async (dados) => {
    try {
      const zip = new JSZip();
      const dataAtual = new Date();
      const dia = dataAtual.getDate();
      const mes = dataAtual.getMonth() + 1;
      const ano = dataAtual.getFullYear();

      for (let i = 0; i < dados.data.length; i++) {
        const itemData = dados.data[i];

        const voucherRows = (itemData.subscriptions || []).map((item) => ({
          Nome: item.user_name || '',
          Entidade: item.user_station_name || '',
          Atividade: item.action_name || '',
          Voucher: item.code || '',
          Emissão: item.createdAt || '',
          Validador: item.validator_identification || '',
          Validação: item.validation_date || '',
        }));

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(voucherRows);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet 1');

        const excelBuffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
        const filename = buildExcelFileName(itemData, filterFormato, dia, mes, ano);
        zip.file(
          filename,
          new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          })
        );
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `relatorios_${filterFormato}_${dia}_${mes}_${ano}.zip`);
    } catch (error) {
      console.error('Não foi possível gerar e compactar os relatórios.', error);
    }
  };

  const gerarPDF = async (dataArray) => {
    try {
      const cabecalhoPreDefinido = [
        'Nome',
        'Entidade',
        'Atividade',
        'Voucher',
        'Emissão',
        'Validador',
        'Validação',
      ];
      const dataAtual = new Date();
      const dia = dataAtual.getDate();
      const mes = dataAtual.getMonth() + 1; // Add 1 because months are zero-indexed
      const ano = dataAtual.getFullYear();

      const pdfBlobs = []; // Array to store PDF blobs for zipping
      const zip = new JSZip(); // Initialize JSZip

      // Define the column mapping once, as it's static
      const columns = cabecalhoPreDefinido.map((headerText) => {
        let dataKey = '';
        switch (headerText) {
          case 'Nome':
            dataKey = 'user_name';
            break;
          case 'Entidade':
            dataKey = 'user_station_name';
            break;
          case 'Atividade':
            dataKey = 'action_name';
            break;
          case 'Voucher':
            dataKey = 'code';
            break;
          case 'Emissão':
            dataKey = 'createdAt';
            break;
          case 'Validador':
            dataKey = 'validator_identification';
            break;
          case 'Validação':
            dataKey = 'validation_date';
            break;
          default:
            dataKey = headerText.toLowerCase().replace(/ /g, '');
        }
        return { header: headerText, dataKey: dataKey };
      });

      // Loop through each activity data item in dataArray.data
      for (const activityData of dataArray.data) {
        const doc = new JsPDF({ orientation: 'landscape' }); // Create a new JsPDF instance for each PDF
        const nomeAtividade = (activityData.name ?? '').replace(/\//g, '-');
        const nameStation = activityData.station_name ?? '';
        const subscriptions = activityData.subscriptions || [];

        const options = {
          margin: { top: 5 },
          headStyles: { fontSize: 10 },
          bodyStyles: { fontSize: 9 },
          startY: 20,
        };

        // Add the title for the current PDF
        if (filterFormato === 'Entidades' || filterFormato === 'Postos') {
          doc.text(`${nameStation} Relatório ${nomeAtividade}-${dia}/${mes}/${ano}`, 14, 10);
        } else if (filterFormato === 'Combustíveis') {
          doc.text(`Total Relatório ${nomeAtividade}-${dia}/${mes}/${ano}`, 14, 10);
        }

        // Create the table for the current PDF
        doc.autoTable({
          columns: columns,
          body: subscriptions,
          ...options,
        });

        // Calculate 'validados' and 'gerados' for the current activity
        let validados = subscriptions.filter((sub) => sub.validated === '1').length;
        let gerados = subscriptions.length;

        let textoResumo = `${validados} Validados: Litros ${
          validados * 20
        }\n${gerados} Gerados: Litros ${gerados * 20}`;
        doc.setFontSize(10);
        doc.text(textoResumo, 14, doc.lastAutoTable.finalY + 10);

        // Generate the PDF as a blob and add it to the zip file
        const pdfName = buildPdfFileName(activityData, filterFormato, dia, mes, ano);
        const pdfBlob = doc.output('blob'); // Output as blob
        zip.file(pdfName, pdfBlob); // Add to zip
      }

      // After all PDFs are generated, generate the zip file
      const zipFileName = `relatorios_${filterFormato}_${dia}_${mes}_${ano}.zip`;
      zip
        .generateAsync({ type: 'blob' })
        .then((content) => {
          // Use FileSaver.js (or similar) to save the zip file
          saveAs(content, zipFileName);
        })
        .catch((error) => {
          console.error('Erro ao gerar o arquivo ZIP.', error);
          // to-do: Re-adicionar toastr.error se a biblioteca estiver disponível.
        });
    } catch (error) {
      console.error('Não foi possível gerar os relatórios.', error);
      // to-do: Re-adicionar toastr.error se a biblioteca estiver disponível.
    }
  };

  const gerarPDFLitros = async (dataArray) => {
    try {
      const cabecalhoPreDefinido = [
        'Gasolina Comum (L)',
        'Gasolina Aditivada (L)',
        'Diesel (L)',
        'Etanol (L)',
        'Podium (L)',
      ];

      const dataAtual = new Date();
      const dia = String(dataAtual.getDate()).padStart(2, '0'); // Formata para 2 dígitos
      const mes = String(dataAtual.getMonth() + 1).padStart(2, '0'); // Meses são baseados em zero, formata para 2 dígitos
      const ano = dataAtual.getFullYear();

      const pdfBlobs = []; // Array para armazenar blobs de PDF (não estritamente necessário se compactando diretamente)
      const zip = new JSZip(); // Inicializa o JSZip

      // 1. Define as colunas com 'header' e 'dataKey' correspondentes às chaves do objeto Litros
      const columns = [
        { header: 'Gasolina Comum (L)', dataKey: 'gasolinaComum' },
        { header: 'Gasolina Aditivada (L)', dataKey: 'gasolinaAditivada' },
        { header: 'Diesel (L)', dataKey: 'diesel' },
        { header: 'Etanol (L)', dataKey: 'etanol' },
        { header: 'Podium (L)', dataKey: 'podium' },
      ];

      // Garante que dataArray.data exista e seja um array antes de iterar
      if (!dataArray || !Array.isArray(dataArray.data)) {
        console.warn(
          'dataArray ou dataArray.data não é um array válido. Pulando a geração de PDF.'
        );
        return;
      }

      // Loop através de cada item de dados da atividade em dataArray.data
      for (const activityData of dataArray.data) {
        const doc = new JsPDF({ orientation: 'landscape' }); // Cria uma nova instância JsPDF para cada PDF
        const nomeAtividade = activityData.name;
        // O objeto 'Litros' contém os dados de uma única linha da tabela
        const litrosData = activityData.Litros; // Renomeado para maior clareza

        const options = {
          margin: { top: 5 },
          headStyles: { fontSize: 10 },
          bodyStyles: { fontSize: 9 },
          startY: 20,
        };

        // Adiciona o título para o PDF atual
        doc.text(`Relatório ${nomeAtividade} - ${dia}/${mes}/${ano}`, 14, 10);

        // Cria a tabela para o PDF atual
        doc.autoTable({
          columns: columns, // Usar o array 'columns' definido acima
          body: [litrosData],
          ...options,
        });
        // Data dos dados
        // Constrói manualmente a data para garantir que seja interpretada no horário local
        const startDate = new Date(filterStartDateReport + 'T00:00:00'); // Início do dia
        const endDate = new Date(filterEndDateReport + 'T23:59:59'); // Fim do dia
        const formattedStartDate = startDate.toLocaleDateString('pt-BR');
        const formattedEndDate = endDate.toLocaleDateString('pt-BR');
        const textoResumo = `Dados referentes ao período de: ${formattedStartDate} 00:00:00 até ${formattedEndDate} 23:59:59`;
        doc.setFontSize(10);
        doc.text(textoResumo, 14, doc.lastAutoTable.finalY + 10);
        // Gera o PDF como um blob e o adiciona ao arquivo zip
        // Sanitiza nomeAtividade e activityData.station para nomes de arquivo
        const safeNomeAtividade = nomeAtividade.replace(/[^a-zA-Z0-9_ -]/g, '');
        const safeStation = activityData.station
          ? String(activityData.station).replace(/[^a-zA-Z0-9_ -]/g, '')
          : 'desconhecida';

        const pdfName = `relatorio_${safeNomeAtividade}_${safeStation}_${dia}_${mes}_${ano}.pdf`;
        const pdfBlob = doc.output('blob'); // Saída como blob
        zip.file(pdfName, pdfBlob); // Adiciona ao zip
      }

      // Após todos os PDFs serem gerados, gera o arquivo zip
      const zipFileName = `relatorios_atividades_${dia}_${mes}_${ano}.zip`;
      zip
        .generateAsync({ type: 'blob' })
        .then((content) => {
          // Use FileSaver.js (ou similar) para salvar o arquivo zip
          saveAs(content, zipFileName);
        })
        .catch((error) => {
          console.error('Erro ao gerar o arquivo ZIP.', error);
          // to-do: Re-adicionar toastr.error se a biblioteca estiver disponível.
        });
    } catch (error) {
      console.error('Não foi possível gerar os relatórios.', error);
      // to-do: Re-adicionar toastr.error se a biblioteca estiver disponível.
    }
  };

  // Função para buscar relatórios da API (envia apenas filtros de backend)
  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      let apiUrl;
      let dadosCombustiveis;
      let response;
      let data;
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);

      if (filterFormato === 'Entidades') {
        if (filterAtividade && filterAtividade !== 'Todas')
          params.append('activity', filterAtividade);
        if (filterStartDate) params.append('startDate', formatDate(filterStartDate));
        if (filterEndDate) params.append('endDate', formatDate(filterEndDate));
        if (filterEntidade && filterEntidade !== 'Todas') params.append('entidade', filterEntidade);
        params.append('tipo', 'tanque_cheio');
        const queryString = params.toString();
        apiUrl = `${BASE_API}/reports-tc-entidade?${queryString}`;
        response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: getAuthHeader(),
          },
        });
        if (!response.ok) {
          throw new Error(`Erro na API: ${response.status} - ${response.statusText}`);
        }

        data = await response.json();
      } else if (filterFormato === 'Postos') {
        const queryString = params.toString();
        apiUrl = `${BASE_API}/reports-tc-postos?${queryString}`;
        response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: getAuthHeader(),
          },
        });

        if (!response.ok) {
          throw new Error(`Erro na API: ${response.status} - ${response.statusText}`);
        }

        data = await response.json();
      } else if (filterFormato === 'Combustíveis') {
        data = {
          totalItems: 5,
          totalPages: 1,
          currentPage: 1,
          pageSize: 5,
          data: [
            {
              id: 1,
              name: 'Gasolina Comum',
            },
            {
              id: 2,
              name: 'Gasolina Aditivada',
            },
            {
              id: 3,
              name: 'Diesel',
            },
            {
              id: 4,
              name: 'Etanol',
            },
            {
              id: 5,
              name: 'Podium',
            },
          ],
        };
      }

      // Preserva o estado 'selected' dos relatórios já existentes na lista
      setReports((prevReports) => {
        return data.data.map((newReport) => {
          const existingReport = prevReports.find((r) => r.id === newReport.id);
          return {
            ...newReport,
            selected: existingReport ? existingReport.selected : false,
          };
        });
      });

      setTotalItems(data.totalItems);
      setTotalPages(data.totalPages);

      // Ajusta a página atual se ela se tornar inválida após a busca
      if (data.totalPages > 0 && currentPage > data.totalPages) {
        setCurrentPage(1);
      } else if (data.totalPages === 0 && currentPage !== 1) {
        // Se não houver itens, garanta que a página seja 1
        setCurrentPage(1);
      }
    } catch (err) {
      console.error('Falha ao buscar relatórios:', err);
      setError('Não foi possível carregar os relatórios. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  }, [
    filterEntidade,
    filterFormato,
    filterAtividade,
    filterStartDate,
    filterEndDate,
    currentPage,
    itemsPerPage,
    getAuthHeader,
  ]); // filterText não é dependência porque é filtro local

  // useEffect para chamar fetchReports quando filtros de backend ou paginação mudam
  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Filtra os relatórios no frontend com base no filterText e filterSelected
  const filteredReportsToDisplay = useMemo(() => {
    let filtered = reports;

    // Filtro por Nome (parcial) - APLICADO NO FRONTEND
    if (filterFormato === 'Entidades' || filterFormato === 'Combustíveis') {
      if (filterText) {
        const lowerCaseFilterText = filterText.toLowerCase();
        filtered = filtered.filter((report) =>
          report.name.toLowerCase().includes(lowerCaseFilterText)
        );
      }
    } else if (filterFormato === 'Postos') {
      if (filterText) {
        const lowerCaseFilterText = filterText.toLowerCase();
        filtered = filtered.filter((report) =>
          report.identification.toLowerCase().includes(lowerCaseFilterText)
        );
      }
    }

    // Filtro por Status de Seleção
    if (filterSelected === 'Sim') {
      filtered = filtered.filter((report) => report.selected);
    }
    if (filterSelected === 'Não') {
      filtered = filtered.filter((report) => !report.selected);
    }

    return filtered;
  }, [reports, filterText, filterSelected]);

  // currentItems são os relatórios já filtrados para exibição
  const currentItems = filteredReportsToDisplay;

  // Função para marcar ou desmarcar todos os itens visíveis na tela
  const handleSelectAll = useCallback(() => {
    // Verifica se todos os itens atualmente visíveis já estão selecionados
    const areAllVisibleSelected =
      currentItems.length > 0 && currentItems.every((item) => item.selected);

    // Cria um Set com os IDs dos itens visíveis para uma busca mais rápida
    const visibleIds = new Set(currentItems.map((item) => item.id));

    setReports((prevReports) =>
      prevReports.map((report) => {
        // Se o relatório atual estiver na lista de itens visíveis
        if (visibleIds.has(report.id)) {
          // A nova seleção será o inverso de "areAllVisibleSelected"
          // Se todos estavam marcados, desmarca (false). Caso contrário, marca (true).
          return { ...report, selected: !areAllVisibleSelected };
        }
        // Mantém o estado dos relatórios que não estão visíveis (em outras páginas)
        return report;
      })
    );
  }, [currentItems, setReports]); // Depende dos itens atuais e da função de setar o estado

  // Relatórios selecionados para download (todos os marcados, independentemente do filtro de texto atual)
  const selectedReportsForDownload = useMemo(() => {
    return reports.filter((report) => report.selected);
  }, [reports]);

  // Função para lidar com o download de cada atividade
  const handleDownload = async () => {
    let response;
    let data;
    let downloadApiUrl;
    if (selectedReportsForDownload.length === 0) {
      alert('Nenhum relatório selecionado para download.');
      return;
    }

    const reportIdsToDownload = selectedReportsForDownload.map((report) => report.id);

    const downloadParams = new URLSearchParams();
    reportIdsToDownload.forEach((id) => downloadParams.append('reportIds', id));

    // Adiciona os filtros de backend ao download, se aplicável
    if (filterStartDateReport)
      downloadParams.append('startDate', formatDate(filterStartDateReport));
    if (filterEndDateReport) downloadParams.append('endDate', formatDate(filterEndDateReport));

    if (filterFormato === 'Combustíveis' && filterPosto && filterPosto !== 'Todas') {
      downloadParams.append('posto', filterPosto);
    }

    const downloadQueryString = downloadParams.toString();
    // Ajuste o endpoint de download se necessário
    if (filterFormato === 'Entidades') {
      downloadApiUrl = `${BASE_API}/download-atividades?${downloadQueryString}`;
    } else if (filterFormato === 'Postos') {
      downloadApiUrl = `${BASE_API}/download-postos?${downloadQueryString}`;
    } else if (filterFormato === 'Combustíveis') {
      downloadApiUrl = `${BASE_API}/download-combustiveis?${downloadQueryString}`;
      console.log('URL de download de combustíveis:', downloadApiUrl);
    }
    try {
      response = await fetch(downloadApiUrl, {
        method: 'GET',
        headers: {
          Authorization: getAuthHeader(),
        },
      });

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status} - ${response.statusText}`);
      }

      data = await response.json();
      if (filterFormato === 'Entidades' || filterFormato === 'Combustíveis') {
        if (downloadFormat === 'Excel') {
          gerarExcel(data);
        } else if (downloadFormat === 'PDF') {
          gerarPDF(data);
        }
      } else if (filterFormato === 'Postos') {
        if (downloadFormat === 'Excel' && filterForma === 'Vouchers') {
          gerarExcel(data);
        } else if (downloadFormat === 'PDF' && filterForma === 'Vouchers') {
          gerarPDF(data);
        } else if (downloadFormat === 'PDF' && filterForma === 'Litros') {
          gerarPDFLitros(data);
        }
      }
    } catch (error) {
      console.error('Erro no download:', error);
      alert(`Erro ao tentar baixar os relatórios: ${error.message || 'Tente novamente.'}`);
    }
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h1>Download de Relatórios</h1>

      {/* Exibindo estados de carregamento/erro */}
      {loading && <p>Carregando relatórios...</p>}
      {error && <p style={{ color: 'red' }}>Erro: {error}</p>}

      {!loading && !error && (
        <>
          {/* Seção de Filtros de Backend e Seleção */}
          <div
            style={{
              marginBottom: '20px',
              border: '1px solid #ccc',
              padding: '15px',
              borderRadius: '8px',
            }}>
            <h2>Filtros</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
              {/* Filtro por Tipo de Unidades (Entidades/Postos) */}
              <div>
                <label htmlFor="filterFormato" style={{ display: 'block', marginBottom: '5px' }}>
                  Tipo de Relatório:
                </label>
                <select
                  id="filterFormato"
                  value={filterFormato}
                  onChange={(e) => {
                    setFilterFormato(e.target.value);
                    setCurrentPage(1); // Reseta a página para a API
                  }}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}>
                  <option value="Entidades">Entidades</option>
                  <option value="Postos">Postos</option>
                  <option value="Combustíveis">Combustíveis</option>
                </select>
              </div>
              {/* Filtro por Data de Início */}
              {filterFormato === 'Entidades' && (
                <div>
                  <label
                    htmlFor="filterStartDate"
                    style={{ display: 'block', marginBottom: '5px' }}>
                    Data Início:
                  </label>
                  <input
                    type="date"
                    id="filterStartDate"
                    value={filterStartDate}
                    onChange={(e) => {
                      setFilterStartDate(e.target.value);
                      setCurrentPage(1); // Reseta a página para a API
                    }}
                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
              )}
              {/* Filtro por Data de Fim */}
              {filterFormato === 'Entidades' && (
                <div>
                  <label htmlFor="filterEndDate" style={{ display: 'block', marginBottom: '5px' }}>
                    Data Fim:
                  </label>
                  <input
                    type="date"
                    id="filterEndDate"
                    value={filterEndDate}
                    onChange={(e) => {
                      setFilterEndDate(e.target.value);
                      setCurrentPage(1); // Reseta a página para a API
                    }}
                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
              )}
              {/* Filtro por Unidades (Entidades) */}
              {filterFormato === 'Entidades' && ( // <-- Essa é a condição de renderização
                <div>
                  <label htmlFor="filterEntidade" style={{ display: 'block', marginBottom: '5px' }}>
                    Entidades:
                  </label>
                  <select
                    id="filterEntidade"
                    value={filterEntidade}
                    onChange={(e) => {
                      setFilterEntidade(e.target.value);
                    }}
                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}>
                    <option value="Todas">Todas</option>
                    <option value="1">SindiLegis</option>
                    <option value="178">SindJus</option>
                    <option value="174">Clubs</option>
                    <option value="148">ASTCU</option>
                  </select>
                </div>
              )}
              {filterFormato === 'Combustíveis' && ( // <-- Essa é a condição de renderização
                <div>
                  <label htmlFor="filterPosto" style={{ display: 'block', marginBottom: '5px' }}>
                    Rede de Postos:
                  </label>
                  <select
                    id="filterPosto"
                    value={filterPosto}
                    onChange={(e) => {
                      setFilterPosto(e.target.value);
                    }}
                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}>
                    <option value="Todas">Todas</option>
                    <option value="Sancler">Sancler</option>
                    <option value="Tanque Cheio">Quality</option>
                  </select>
                </div>
              )}
              {filterFormato === 'Postos' && (
                <div>
                  <label htmlFor="filterForma" style={{ display: 'block', marginBottom: '5px' }}>
                    Parâmetro:
                  </label>
                  <select
                    id="filterForma"
                    value={filterForma}
                    onChange={(e) => {
                      setfFilterForma(e.target.value);
                    }}
                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}>
                    <option value="Vouchers">Vouchers</option>
                    <option value="Litros">Litros</option>
                  </select>
                </div>
              )}
              {/* Filtro por Status de Seleção (frontend) */}
              <div>
                <label htmlFor="filterSelected" style={{ display: 'block', marginBottom: '5px' }}>
                  Status de Seleção:
                </label>
                <select
                  id="filterSelected"
                  value={filterSelected}
                  onChange={(e) => {
                    setFilterSelected(e.target.value);
                  }}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}>
                  <option value="Todos">Todos</option>
                  <option value="Sim">Selecionados</option>
                  <option value="Não">Não Selecionados</option>
                </select>
              </div>
            </div>
          </div>

          {/* Ações de Download com Caixa de Seleção para formato */}
          <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Filtro por Data de Início dos relatorios */}
            {(filterFormato === 'Combustíveis' || filterFormato === 'Postos') && (
              <div>
                <label
                  htmlFor="filterStartDateReport"
                  style={{ display: 'block', marginBottom: '5px' }}>
                  Data Início:
                </label>
                <input
                  type="date"
                  id="filterStartDateReport"
                  value={filterStartDateReport}
                  onChange={(e) => {
                    setFilterStartDateReport(e.target.value);
                    setCurrentPage(1); // Reseta a página para a API
                  }}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>
            )}
            {/* Filtro por Data de Fim dos relatorios*/}
            {(filterFormato === 'Combustíveis' || filterFormato === 'Postos') && (
              <div>
                <label
                  htmlFor="filterEndDateReport"
                  style={{ display: 'block', marginBottom: '5px' }}>
                  Data Fim:
                </label>
                <input
                  type="date"
                  id="filterEndDateReport"
                  value={filterEndDateReport}
                  onChange={(e) => {
                    setFilterEndDateReport(e.target.value);
                    setCurrentPage(1); // Reseta a página para a API
                  }}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>
            )}
            <div>
              <label htmlFor="downloadFormat" style={{ display: 'block', marginBottom: '5px' }}>
                Baixar como:
              </label>
              <select
                id="downloadFormat"
                value={downloadFormat}
                onChange={(e) => setDownloadFormat(e.target.value)}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}>
                <option value="PDF">Todos Selecionados (PDF)</option>
                <option value="Excel">Todos Selecionados (Excel)</option>
              </select>
            </div>
            <div>
              <button
                onClick={handleDownload}
                disabled={selectedReportsForDownload.length === 0}
                style={{
                  padding: '10px 15px',
                  borderRadius: '10px',
                  border: 'none',
                  backgroundColor: '#007bff',
                  color: 'white',
                  cursor: 'pointer',
                }}>
                Baixar Selecionados
              </button>
            </div>
          </div>

          {/* NOVO: Filtro por Nome e Botão para Marcar/Desmarcar Todos */}
          <div
            style={{
              marginBottom: '20px',
              marginTop: '20px',
              padding: '10px',
              border: '1px solid #eee',
              borderRadius: '5px',
            }}>
            <label
              htmlFor="filterTextList"
              style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Filtrar por Nome na Lista (local):
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="text"
                id="filterTextList"
                placeholder="Buscar por nome na lista atual"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  width: '100%',
                  flexGrow: 1, // Faz o input ocupar o espaço disponível
                }}
              />
              <button
                onClick={handleSelectAll}
                disabled={currentItems.length === 0}
                style={{
                  padding: '8px 15px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  backgroundColor: '#f0f0f0',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap', // Impede que o texto quebre
                }}>
                {/* Texto dinâmico do botão */}
                {currentItems.length > 0 && currentItems.every((item) => item.selected)
                  ? 'Desmarcar Todos'
                  : 'Marcar Todos'}
              </button>
            </div>
          </div>

          {/* Lista de Relatórios */}
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {currentItems.length > 0 ? (
              currentItems.map((report) => (
                <li
                  key={report.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '10px',
                    padding: '10px',
                    border: '1px solid #eee',
                    borderRadius: '5px',
                    backgroundColor: report.selected ? '#e6f7ff' : '#fff',
                  }}>
                  <input
                    type="checkbox"
                    checked={report.selected}
                    onChange={() => handleCheckboxChange(report.id)}
                    style={{ marginRight: '10px', transform: 'scale(1.2)' }}
                  />
                  {filterFormato === 'Entidades' && (
                    <span style={{ flexGrow: 1 }}>
                      <strong>{report.name}</strong> - Entidade: {report.station?.name || 'N/A'} -
                      Data:
                      {new Date(report.start).toLocaleString('pt-BR', {
                        timeZone: 'America/Sao_Paulo',
                      }) || 'N/A'}
                    </span>
                  )}
                  {filterFormato === 'Postos' && (
                    <span style={{ flexGrow: 1 }}>
                      <strong>{report.identification}</strong> - Código: {report.code || 'N/A'}
                    </span>
                  )}
                  {filterFormato === 'Combustíveis' && (
                    <span style={{ flexGrow: 1 }}>
                      <strong>{report.name}</strong>
                    </span>
                  )}
                </li>
              ))
            ) : (
              <p>Nenhum relatório encontrado com os filtros aplicados na página atual.</p>
            )}
          </ul>

          {/* Controles de Paginação */}
          {totalPages > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: '20px',
                gap: '10px',
              }}>
              <label htmlFor="itemsPerPageSelect" style={{ whiteSpace: 'nowrap' }}>
                Itens por página:
              </label>
              <select
                id="itemsPerPageSelect"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}>
                <option value={5}>5</option>
                <option value={15}>15</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>

              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 15px',
                  borderRadius: '5px',
                  border: '1px solid #ccc',
                  backgroundColor: '#f0f0f0',
                  cursor: 'pointer',
                }}>
                Anterior
              </button>

              {Array.from({ length: totalPages }).map((_, index) => {
                const pageNumber = index + 1;
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
                ) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => paginate(pageNumber)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '5px',
                        border: `1px solid ${currentPage === pageNumber ? '#007bff' : '#ccc'}`,
                        backgroundColor: currentPage === pageNumber ? '#007bff' : '#fff',
                        color: currentPage === pageNumber ? 'white' : 'black',
                        cursor: 'pointer',
                      }}>
                      {pageNumber}
                    </button>
                  );
                } else if (
                  (pageNumber === currentPage - 3 && currentPage > 3) ||
                  (pageNumber === currentPage + 3 && currentPage < totalPages - 2)
                ) {
                  return (
                    <span key={pageNumber} style={{ margin: '0 5px' }}>
                      ...
                    </span>
                  );
                }
                return null;
              })}

              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 15px',
                  borderRadius: '5px',
                  border: '1px solid #ccc',
                  backgroundColor: '#f0f0f0',
                  cursor: 'pointer',
                }}>
                Próxima
              </button>
              <span style={{ marginLeft: '10px' }}> Total de itens: {totalItems} </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReportList;
